/**
 * Cálculo de ofertas para preview en carrito (frontend).
 * Espeja OfertaCalculationService: NxM agrupa por oferta (más barata gratis);
 * por_cantidad y porcentaje por línea; conflicto greedy.
 */

const TIPOS_NXM = {
  "2x1": 2,
  "3x2": 3,
  "4x3": 4,
};

function round2(n) {
  return Math.round(n * 100) / 100;
}

function normId(id) {
  return id != null ? String(id) : null;
}

function entityId(entity) {
  if (entity == null) return null;
  if (typeof entity === "object") {
    return normId(entity.id || entity._id);
  }
  return normId(entity);
}

/**
 * Verifica si producto cumple criterios de oferta (OR: productos | marcas | modelos | tamanios)
 */
export function productoCumpleCriterios(producto, criterios) {
  if (!criterios || !producto) return false;

  if (criterios.productos && criterios.productos.length > 0) {
    const id = entityId(producto);
    if (id && criterios.productos.some((p) => entityId(p) === id)) {
      return true;
    }
  }

  let cumple = false;

  if (criterios.marcas && criterios.marcas.length > 0 && producto.marca) {
    const marcaId = entityId(producto.marca);
    if (marcaId && criterios.marcas.some((m) => entityId(m) === marcaId)) {
      cumple = true;
    }
  }

  if (criterios.modelos && criterios.modelos.length > 0 && producto.modelo) {
    const modeloId = entityId(producto.modelo);
    if (modeloId && criterios.modelos.some((m) => entityId(m) === modeloId)) {
      cumple = true;
    }
  }

  if (criterios.tamanios && criterios.tamanios.length > 0 && producto.tamanio) {
    const tamanioId = entityId(producto.tamanio);
    if (tamanioId && criterios.tamanios.some((t) => entityId(t) === tamanioId)) {
      cumple = true;
    }
  }

  return cumple;
}

function expandirUnidades(itemsElegibles) {
  const unidades = [];
  for (const item of itemsElegibles) {
    const qty = Math.max(0, Math.floor(Number(item.cantidad) || 0));
    const precio = Number(item.precioBase) || 0;
    for (let u = 0; u < qty; u++) {
      unidades.push({ itemIndex: item.index, precio });
    }
  }
  return unidades;
}

function asignarUnidadesGratis(unidades, cantidadGratis, itemsElegibles) {
  if (!unidades.length || !cantidadGratis || cantidadGratis <= 0) return null;

  const sorted = [...unidades].sort((a, b) => a.precio - b.precio);
  const freeCount = Math.min(cantidadGratis, sorted.length);

  const porItem = new Map();
  for (const item of itemsElegibles) {
    porItem.set(item.index, {
      precioTotal: 0,
      descuentoAplicado: 0,
    });
  }

  for (const u of sorted) {
    const line = porItem.get(u.itemIndex);
    if (line) line.precioTotal += u.precio;
  }

  for (let i = 0; i < freeCount; i++) {
    const u = sorted[i];
    const line = porItem.get(u.itemIndex);
    if (!line) continue;
    line.precioTotal -= u.precio;
    line.descuentoAplicado += u.precio;
  }

  let descuentoTotal = 0;
  for (const [, line] of porItem) {
    line.precioTotal = round2(line.precioTotal);
    line.descuentoAplicado = round2(line.descuentoAplicado);
    descuentoTotal += line.descuentoAplicado;
  }

  if (descuentoTotal <= 0) return null;
  return { porItem, descuentoTotal: round2(descuentoTotal) };
}

function aplicarOfertaNxMAgrupo(oferta, itemsElegibles) {
  const N = TIPOS_NXM[oferta && oferta.tipo];
  if (!N || !itemsElegibles || !itemsElegibles.length) return null;
  const unidades = expandirUnidades(itemsElegibles);
  const cantidadGratis = Math.floor(unidades.length / N);
  return asignarUnidadesGratis(unidades, cantidadGratis, itemsElegibles);
}

function aplicarOfertaPorCantidadAgrupo(oferta, itemsElegibles) {
  if (!oferta || !itemsElegibles || !itemsElegibles.length) return null;

  const umbral =
    oferta.cantidadMinima != null && Number.isFinite(Number(oferta.cantidadMinima))
      ? Math.max(1, Math.floor(Number(oferta.cantidadMinima)))
      : null;
  const valor = oferta.valor;

  if (umbral == null || valor == null || valor < 0 || valor > 100) {
    return null;
  }

  const porItem = new Map();
  let descuentoTotal = 0;
  for (const item of itemsElegibles) {
    const cantidad = Math.max(0, Number(item.cantidad) || 0);
    if (cantidad < umbral) continue;

    const base = (Number(item.precioBase) || 0) * cantidad;
    const descuento = base * (valor / 100);
    if (descuento <= 0) continue;

    porItem.set(item.index, {
      precioTotal: round2(base - descuento),
      descuentoAplicado: round2(descuento),
    });
    descuentoTotal += descuento;
  }

  if (porItem.size === 0 || descuentoTotal <= 0) return null;
  return { porItem, descuentoTotal: round2(descuentoTotal) };
}

function calcularAplicacionGrupal(oferta, itemsElegibles) {
  if (oferta.tipo === "por_cantidad") {
    return aplicarOfertaPorCantidadAgrupo(oferta, itemsElegibles);
  }
  if (TIPOS_NXM[oferta.tipo]) {
    return aplicarOfertaNxMAgrupo(oferta, itemsElegibles);
  }
  return null;
}

function calcPorcentajeLinea(precioBase, cantidad, valor) {
  if (valor == null || valor < 0 || valor > 100) {
    return { precioTotal: precioBase * cantidad, descuentoAplicado: 0 };
  }
  const base = precioBase * cantidad;
  const descuento = base * (valor / 100);
  return {
    precioTotal: round2(base - descuento),
    descuentoAplicado: round2(descuento),
  };
}

function buildOfertaAplicada(oferta, descuentoAplicado) {
  return {
    id: oferta.id || oferta._id || null,
    nombre: oferta.nombre,
    tipo: oferta.tipo,
    valor: oferta.valor != null ? oferta.valor : null,
    cantidadMinima: oferta.cantidadMinima != null ? oferta.cantidadMinima : null,
    descuento: round2(descuentoAplicado || 0),
    descuentoAplicado: round2(descuentoAplicado || 0),
  };
}

/**
 * Aplica ofertas al carrito (preview).
 * @param {Array<{producto, cantidad, precioBase}>} items
 * @param {Array} ofertas - ya filtradas como aplicables al movimiento
 * @returns {Array<{precioTotal, ofertaAplicada}>}
 */
export function aplicarOfertasACarritoPreview(items, ofertas) {
  if (!items || items.length === 0) return [];

  const n = items.length;
  const resultado = items.map((item) => {
    const cantidad = Number(item.cantidad) || 0;
    const precioBase = Number(item.precioBase) || 0;
    return {
      precioTotal: precioBase * cantidad,
      ofertaAplicada: null,
    };
  });
  const asignado = new Array(n).fill(false);
  const ofertasList = ofertas || [];

  const candidatosGrupo = [];
  for (const oferta of ofertasList) {
    if (
      oferta.tipo === "porcentaje" ||
      (!TIPOS_NXM[oferta.tipo] && oferta.tipo !== "por_cantidad")
    ) {
      continue;
    }

    const elegibles = [];
    for (let i = 0; i < n; i++) {
      const producto = items[i].producto;
      if (productoCumpleCriterios(producto, oferta.criterios)) {
        elegibles.push({
          index: i,
          cantidad: Number(items[i].cantidad) || 0,
          precioBase: Number(items[i].precioBase) || 0,
        });
      }
    }
    if (elegibles.length === 0) continue;

    const application = calcularAplicacionGrupal(oferta, elegibles);
    if (!application || application.descuentoTotal <= 0) continue;

    candidatosGrupo.push({
      oferta,
      elegibles,
      descuentoTotal: application.descuentoTotal,
    });
  }

  candidatosGrupo.sort((a, b) => b.descuentoTotal - a.descuentoTotal);

  for (const candidate of candidatosGrupo) {
    const libres = candidate.elegibles.filter((e) => !asignado[e.index]);
    if (libres.length === 0) continue;

    const application = calcularAplicacionGrupal(candidate.oferta, libres);
    if (!application || application.descuentoTotal <= 0) continue;

    for (const [itemIndex, lineResult] of application.porItem) {
      resultado[itemIndex].precioTotal = lineResult.precioTotal;
      resultado[itemIndex].ofertaAplicada = buildOfertaAplicada(
        candidate.oferta,
        lineResult.descuentoAplicado
      );
      asignado[itemIndex] = true;
    }
  }

  for (let i = 0; i < n; i++) {
    if (asignado[i]) continue;

    const producto = items[i].producto;
    const precioBase = Number(items[i].precioBase) || 0;
    const cantidad = Number(items[i].cantidad) || 0;
    let mejorOferta = null;
    let mejorCalc = null;

    for (const oferta of ofertasList) {
      if (oferta.tipo !== "porcentaje") continue;
      if (!productoCumpleCriterios(producto, oferta.criterios)) continue;
      const calc = calcPorcentajeLinea(precioBase, cantidad, oferta.valor);
      if (calc.descuentoAplicado > 0 && (!mejorCalc || calc.descuentoAplicado > mejorCalc.descuentoAplicado)) {
        mejorOferta = oferta;
        mejorCalc = calc;
      }
    }

    if (mejorOferta && mejorCalc) {
      resultado[i].precioTotal = mejorCalc.precioTotal;
      resultado[i].ofertaAplicada = buildOfertaAplicada(
        mejorOferta,
        mejorCalc.descuentoAplicado
      );
    }
  }

  return resultado;
}

/**
 * Calcula precio de una línea con oferta (compat single-line; preferir preview agrupado).
 */
export function calcularPrecioConOferta(tipo, precioBase, cantidad, valor = null, cantidadMinima = null) {
  if (!precioBase || !cantidad || cantidad <= 0) {
    return precioBase * cantidad;
  }

  switch (tipo) {
    case "porcentaje":
      return calcPorcentajeLinea(precioBase, cantidad, valor).precioTotal;
    case "por_cantidad": {
      const umbral =
        cantidadMinima != null && Number.isFinite(Number(cantidadMinima))
          ? Math.max(1, Math.floor(Number(cantidadMinima)))
          : null;
      if (
        umbral != null &&
        cantidad >= umbral &&
        valor != null &&
        valor >= 0 &&
        valor <= 100
      ) {
        return calcPorcentajeLinea(precioBase, cantidad, valor).precioTotal;
      }
      return precioBase * cantidad;
    }
    case "2x1": {
      const grupos = Math.floor(cantidad / 2);
      const restantes = cantidad % 2;
      return grupos * precioBase + restantes * precioBase;
    }
    case "3x2": {
      const grupos = Math.floor(cantidad / 3);
      const restantes = cantidad % 3;
      return grupos * 2 * precioBase + restantes * precioBase;
    }
    case "4x3": {
      const grupos = Math.floor(cantidad / 4);
      const restantes = cantidad % 4;
      return grupos * 3 * precioBase + restantes * precioBase;
    }
    default:
      return precioBase * cantidad;
  }
}
